import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { changePassword, logoutAll } from '@/api/auth'
import { useAuth } from '@/auth/useAuth'
import PasswordInput from '@/components/PasswordInput'
import type { ApiError } from '@/types'

const MIN_PASSWORD_LENGTH = 8
const MAX_PASSWORD_LENGTH = 64
const NON_PRINTABLE_ASCII_PATTERN = /[^\x20-\x7E]/u
const ASCII_PASSWORD_WARNING = 'Password may contain only printable ASCII characters.'

export function containsNonPrintableAscii(value: string): boolean {
  return NON_PRINTABLE_ASCII_PATTERN.test(value)
}

export function validateNewPassword(password: string, confirm: string): string | null {
  if (containsNonPrintableAscii(password) || containsNonPrintableAscii(confirm)) {
    return ASCII_PASSWORD_WARNING
  }
  if (password.length < MIN_PASSWORD_LENGTH || password.length > MAX_PASSWORD_LENGTH) {
    return `New password must be ${MIN_PASSWORD_LENGTH}-${MAX_PASSWORD_LENGTH} characters.`
  }
  if (password !== confirm) {
    return 'New password confirmation does not match.'
  }
  return null
}

export default function ChangePasswordPage() {
  const { logout } = useAuth()
  const navigate = useNavigate()
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [loggingOutAll, setLoggingOutAll] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function finishAndReturnToLogin(message: string) {
    setCurrentPassword('')
    setNewPassword('')
    setConfirmPassword('')
    logout()
    navigate('/login', { replace: true, state: { message } })
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    const validationError = validateNewPassword(newPassword, confirmPassword)
    if (validationError) {
      setError(validationError)
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      await changePassword({
        current_password: currentPassword,
        new_password: newPassword,
        new_password_confirm: confirmPassword,
      })
      finishAndReturnToLogin('Password changed. Sign in again with your new password.')
    } catch (caught: unknown) {
      setError((caught as ApiError)?.message ?? 'Password change failed.')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleLogoutAll() {
    if (!window.confirm('Sign out every browser and device using this account?')) return
    setLoggingOutAll(true)
    setError(null)
    try {
      await logoutAll()
      finishAndReturnToLogin('All sessions were signed out.')
    } catch (caught: unknown) {
      setError((caught as ApiError)?.message ?? 'Unable to sign out all sessions.')
    } finally {
      setLoggingOutAll(false)
    }
  }

  return (
    <div style={{ maxWidth: 620 }}>
      <h1 style={{ margin: '0 0 5px', fontSize: 22 }}>Change Password</h1>
      <p style={{ margin: '0 0 20px', color: '#777', fontSize: 13 }}>
        Changing your password expires all existing sessions, including this one.
      </p>

      <form onSubmit={handleSubmit} style={cardStyle}>
        <div style={fieldStyle}>
          <label htmlFor="current-password" style={labelTextStyle}>Current password</label>
          <PasswordInput
            id="current-password"
            accessibleName="current password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={setCurrentPassword}
          />
        </div>
        <div style={fieldStyle}>
          <label htmlFor="new-password" style={labelTextStyle}>New password</label>
          <PasswordInput
            id="new-password"
            accessibleName="new password"
            autoComplete="new-password"
            value={newPassword}
            onChange={setNewPassword}
            minLength={MIN_PASSWORD_LENGTH}
            maxLength={MAX_PASSWORD_LENGTH}
          />
          {containsNonPrintableAscii(newPassword) && (
            <PasswordWarning>{ASCII_PASSWORD_WARNING}</PasswordWarning>
          )}
          <span style={helpStyle}>
            8-64 printable ASCII characters (space through ~).
          </span>
        </div>
        <div style={fieldStyle}>
          <label htmlFor="confirm-new-password" style={labelTextStyle}>
            Confirm new password
          </label>
          <PasswordInput
            id="confirm-new-password"
            accessibleName="confirm new password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={setConfirmPassword}
            minLength={MIN_PASSWORD_LENGTH}
            maxLength={MAX_PASSWORD_LENGTH}
          />
          {containsNonPrintableAscii(confirmPassword) && (
            <PasswordWarning>{ASCII_PASSWORD_WARNING}</PasswordWarning>
          )}
        </div>
        {error && <div style={errorStyle}>{error}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="submit"
            disabled={submitting || loggingOutAll}
            style={primaryButtonStyle}
          >
            {submitting ? 'Changing...' : 'Change password'}
          </button>
        </div>
      </form>

      <div style={{ ...cardStyle, marginTop: 16 }}>
        <h2 style={{ margin: '0 0 6px', fontSize: 15 }}>Sign out all sessions</h2>
        <p style={{ margin: '0 0 14px', color: '#666', fontSize: 12 }}>
          Invalidate every current token without changing your password.
        </p>
        <button
          type="button"
          onClick={handleLogoutAll}
          disabled={submitting || loggingOutAll}
          style={dangerButtonStyle}
        >
          {loggingOutAll ? 'Signing out...' : 'Sign out all sessions'}
        </button>
      </div>
    </div>
  )
}

function PasswordWarning({ children }: { children: React.ReactNode }) {
  return (
    <div role="alert" style={passwordWarningStyle}>
      <span aria-hidden="true" style={passwordWarningArrowStyle} />
      {children}
    </div>
  )
}

const cardStyle: React.CSSProperties = { padding: 20, border: '1px solid #ddd', borderRadius: 6, background: '#fff' }
const fieldStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 15 }
const labelTextStyle: React.CSSProperties = { color: '#444', fontSize: 13, fontWeight: 600 }
const helpStyle: React.CSSProperties = { color: '#777', fontSize: 11, fontWeight: 400 }
const passwordWarningStyle: React.CSSProperties = { position: 'relative', marginTop: 3, padding: '8px 10px', border: '1px solid #ffb74d', borderRadius: 4, background: '#fff8e1', color: '#8a4b08', fontSize: 12, fontWeight: 500 }
const passwordWarningArrowStyle: React.CSSProperties = { position: 'absolute', top: -5, left: 14, width: 8, height: 8, transform: 'rotate(45deg)', borderTop: '1px solid #ffb74d', borderLeft: '1px solid #ffb74d', background: '#fff8e1' }
const errorStyle: React.CSSProperties = { marginBottom: 14, padding: '9px 11px', borderRadius: 4, background: '#fdecea', color: '#c62828', fontSize: 12 }
const primaryButtonStyle: React.CSSProperties = { padding: '8px 16px', border: 'none', borderRadius: 4, background: '#3949ab', color: '#fff', cursor: 'pointer', fontFamily: 'inherit' }
const dangerButtonStyle: React.CSSProperties = { padding: '7px 14px', border: '1px solid #ef9a9a', borderRadius: 4, background: '#fff', color: '#c62828', cursor: 'pointer', fontFamily: 'inherit' }
